import { 
  createSlice, 
  createAsyncThunk, 
  createSelector, 
  PayloadAction} from '@reduxjs/toolkit';
import { RootState } from '../index';
// Import the types from the types folder
import { Competition, CompetitionStats, CompetitionType, CompetitionMatch as OriginalCompetitionMatch } from '../../types/competition';
import logger from '../../utils/logger';

// Define a matching CompetitionMatch type for our Redux slice
type CompetitionMatch = OriginalCompetitionMatch;
// Define initial state for the competition slice
interface CompetitionState {
  competitions: Record<string, Competition>;
  competitionIds: string[];
  matches: Record<string, CompetitionMatch>;
  stats: CompetitionStats | null;
  friendsCompetitions: Record<string, Record<string, Competition>>;
  isLoading: boolean;
  error: string | null;
}
const initialState: CompetitionState = {
  competitions: {},
  competitionIds: [],
  matches: {},
  stats: null,
  friendsCompetitions: {},
  isLoading: false,
  error: null
};
// Async thunks for competition operations
export const fetchUserCompetitions = createAsyncThunk(
  'competitions/fetchUserCompetitions',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Import dynamically to avoid circular imports
      const { getUserCompetitions } = await import('../../services/firebase/competition.service');
      
      // Fetch real competitions from Firebase
      const competitions = await getUserCompetitions(userId);
      
      // Convert array to Record<string, Competition> format for Redux
      const competitionsRecord: Record<string, Competition> = {};
      competitions.forEach((comp) => {
        if (comp.id) {
          // Store the competition directly since types are now aligned
          competitionsRecord[comp.id] = comp as Competition;
        }
      });
      
      logger.info('[CompetitionSlice] Fetched real competitions:', 
                 competitions.length, 
                 'first competition:', 
                 competitions.length > 0 ? competitions[0].tournamentName : 'none');
      
      return {
        competitions: competitionsRecord,
        competitionIds: Object.keys(competitionsRecord)
      };
    } catch (error) {
      logger.error('[CompetitionSlice] Error fetching competitions:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);
export const fetchCompetitionStats = createAsyncThunk(
  'competitions/fetchCompetitionStats',
  async (userId: string, { rejectWithValue, getState }) => {
    try {
      // Import helpers and access to state
      const state = getState() as RootState;
      
      // Use competitions from state to calculate real stats
      const competitions = Object.values(state.competitions.competitions);
      
      // Calculate stats from actual competition data
      let totalGold = 0;
      let totalSilver = 0;
      let totalBronze = 0;
      let totalWins = 0;
      let totalLosses = 0;
      const competitionTypeCounts: Record<string, number> = {
        bjj: 0,
        judo: 0,
        wrestling: 0,
        mma: 0,
        boxing: 0,
        kickboxing: 0,
        other: 0
      };
      
      // Stats by year
      const byYear: Record<string, number> = {};
      
      // Location frequency to find most successful
      const locationCounts: Record<string, number> = {};
      let mostSuccessfulLocation = '';
      let mostSuccessfulCount = 0;
      
      // Latest competition date
      let lastCompetitionDate = 0;
      
      // Process each competition
      competitions.forEach(comp => {
        // Count medals
        if (comp.rank) {
          if (comp.rank === 1) totalGold++;
          if (comp.rank === 2) totalSilver++;
          if (comp.rank === 3) totalBronze++;
        }
        
        // Add wins and losses
        totalWins += comp.winsCount || 0;
        totalLosses += comp.lossesCount || 0;
        
        // Count competition types
        if (comp.type && competitionTypeCounts.hasOwnProperty(comp.type)) {
          competitionTypeCounts[comp.type]++;
        } else {
          competitionTypeCounts.other++;
        }
        
        // Group by year
        const year = new Date(comp.date).getFullYear().toString();
        byYear[year] = (byYear[year] || 0) + 1;
        
        // Track location frequency
        const locationName = typeof comp.location === 'object' ? comp.location.name : comp.location;
        if (locationName) {
          locationCounts[locationName] = (locationCounts[locationName] || 0) + 1;
          if (locationCounts[locationName] > mostSuccessfulCount) {
            mostSuccessfulLocation = locationName;
            mostSuccessfulCount = locationCounts[locationName];
          }
        }
        
        // Track latest competition date
        if (comp.date > lastCompetitionDate) {
          lastCompetitionDate = comp.date;
        }
      });
      
      // Calculate win percentage
      const totalMatches = totalWins + totalLosses;
      const winPercentage = totalMatches > 0 ? Math.round((totalWins / totalMatches) * 100) : 0;
      
      const stats: CompetitionStats = {
        userId,
        totalCompetitions: competitions.length,
        medals: {
          gold: totalGold,
          silver: totalSilver,
          bronze: totalBronze,
          total: totalGold + totalSilver + totalBronze
        },
        wins: totalWins,
        losses: totalLosses,
        winPercentage,
        byType: competitionTypeCounts as Record<CompetitionType, number>,
        byYear,
        mostSuccessfulLocation,
        lastCompetitionDate
      };
      
      logger.info('[CompetitionSlice] Calculated competition stats:', stats);
      
      return stats;
    } catch (error) {
      logger.error('[CompetitionSlice] Error calculating competition stats:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);
export const fetchFriendCompetitions = createAsyncThunk(
  'competitions/fetchFriendCompetitions',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Import dynamically to avoid circular imports
      const { getUserCompetitions } = await import('../../services/firebase/competition.service');
      
      // Fetch friend's competitions from Firebase
      const competitions = await getUserCompetitions(userId);
      
      // Convert array to Record<string, Competition> format for Redux
      const competitionsRecord: Record<string, Competition> = {};
      competitions.forEach((comp) => {
        if (comp.id) {
          // Store the competition directly since types are now aligned
          competitionsRecord[comp.id] = comp as Competition;
        }
      });
      
      logger.info('[CompetitionSlice] Fetched friend competitions:', 
                 competitions.length, 
                 'for user:', userId);
      
      return {
        userId,
        competitions: competitionsRecord
      };
    } catch (error) {
      logger.error('[CompetitionSlice] Error fetching friend competitions:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);
export const addCompetition = createAsyncThunk(
  'competitions/addCompetition',
  async (competitionData: Omit<Competition, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      // Import dynamically to avoid circular imports
      const { createCompetition, getCompetition } = await import('../../services/firebase/competition.service');

      // Create the competition in Firebase
      const competitionId = await createCompetition(competitionData);
      
      // Fetch the created competition to ensure we have the correct data format
      const newCompetition = await getCompetition(competitionId);
      
      if (!newCompetition) {
        throw new Error('Failed to retrieve new competition after creation');
      }
      
      logger.info('[CompetitionSlice] Created new competition:', newCompetition.tournamentName, 'with ID:', competitionId);
      
      return newCompetition;
    } catch (error) {
      logger.error('[CompetitionSlice] Error creating competition:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);
export const updateCompetition = createAsyncThunk(
  'competitions/updateCompetition',
  async ({ id, data }: { id: string; data: Partial<Competition> }, { rejectWithValue }) => {
    try {
      // Import dynamically to avoid circular imports
      const { updateCompetition: updateCompetitionService, getCompetition } = await import('../../services/firebase/competition.service');
      
      // Update the competition in Firebase
      await updateCompetitionService(id, {
        ...data,
        dateUpdated: Date.now()
      });
      
      // Fetch the updated competition to ensure we have the correct data
      const updatedCompetition = await getCompetition(id);
      
      if (!updatedCompetition) {
        throw new Error('Failed to retrieve updated competition');
      }
      
      logger.info('[CompetitionSlice] Updated competition:', updatedCompetition.tournamentName, 'with ID:', id);
      
      // Convert back to store format
      return {
        ...updatedCompetition,
        userId: updatedCompetition.owner || '',
        createdAt: updatedCompetition.dateCreated || Date.now(),
        updatedAt: updatedCompetition.dateUpdated || updatedCompetition.dateCreated || Date.now()
      } as Competition;
    } catch (error) {
      logger.error('[CompetitionSlice] Error updating competition:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);
export const deleteCompetition = createAsyncThunk(
  'competitions/deleteCompetition',
  async (id: string, { rejectWithValue }) => {
    try {
      // Import dynamically to avoid circular imports
      const { deleteCompetition: deleteCompetitionService } = await import('../../services/firebase/competition.service');
      
      // Delete the competition in Firebase
      await deleteCompetitionService(id);
      
      logger.info('[CompetitionSlice] Deleted competition with ID:', id);
      
      return id;
    } catch (error) {
      logger.error('[CompetitionSlice] Error deleting competition:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);
// Create the competition slice
const competitionSlice = createSlice({
  name: 'competitions',
  initialState,
  reducers: {
    clearCompetitions: (state) => {
      state.competitions = {};
      state.competitionIds = [];
    },
    clearFriendCompetitions: (state, action: PayloadAction<string>) => {
      const userId = action.payload;
      if (state.friendsCompetitions[userId]) {
        delete state.friendsCompetitions[userId];
      }
    },
    clearAllFriendCompetitions: (state) => {
      state.friendsCompetitions = {};
    },
    resetError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserCompetitions
      .addCase(fetchUserCompetitions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserCompetitions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.competitions = action.payload.competitions;
        state.competitionIds = action.payload.competitionIds;
      })
      .addCase(fetchUserCompetitions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // fetchCompetitionStats
      .addCase(fetchCompetitionStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompetitionStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchCompetitionStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // fetchFriendCompetitions
      .addCase(fetchFriendCompetitions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFriendCompetitions.fulfilled, (state, action) => {
        state.isLoading = false;
        const { userId, competitions } = action.payload;
        state.friendsCompetitions = {
          ...state.friendsCompetitions,
          [userId]: competitions
        };
      })
      .addCase(fetchFriendCompetitions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // addCompetition
      .addCase(addCompetition.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addCompetition.fulfilled, (state, action) => {
        state.isLoading = false;
        const newCompetition = action.payload as any; // Type casting to bypass strict type checking
        state.competitions[newCompetition.id] = newCompetition;
        state.competitionIds.push(newCompetition.id);
      })
      .addCase(addCompetition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateCompetition
      .addCase(updateCompetition.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompetition.fulfilled, (state, action) => {
        state.isLoading = false;
        // Type cast to any to avoid type incompatibilities with Timestamp
        const updatedCompetition = action.payload as any;
        if (state.competitions[updatedCompetition.id]) {
          state.competitions[updatedCompetition.id] = {
            ...state.competitions[updatedCompetition.id],
            ...updatedCompetition
          };
        }
      })
      .addCase(updateCompetition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // deleteCompetition
      .addCase(deleteCompetition.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCompetition.fulfilled, (state, action) => {
        state.isLoading = false;
        const id = action.payload;
        delete state.competitions[id];
        state.competitionIds = state.competitionIds.filter(competitionId => competitionId !== id);
      })
      .addCase(deleteCompetition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});
// Export actions
export const { 
  clearCompetitions, 
  clearFriendCompetitions, 
  clearAllFriendCompetitions,
  resetError 
} = competitionSlice.actions;
// Selectors
export const selectCompetitionState = (state: RootState) => state.competitions;
export const selectCompetitions = createSelector(
  selectCompetitionState,
  (state) => state.competitions
);
export const selectCompetitionIds = createSelector(
  selectCompetitionState,
  (state) => state.competitionIds
);
export const selectCompetitionById = (id: string) => createSelector(
  selectCompetitions,
  (competitions) => competitions[id] || null
);
export const selectCompetitionStats = createSelector(
  selectCompetitionState,
  (state) => state.stats
);
export const selectFriendsCompetitions = createSelector(
  selectCompetitionState,
  (state) => state.friendsCompetitions
);
export const selectFriendCompetitionsByUserId = (userId: string) => createSelector(
  selectFriendsCompetitions,
  (friendsCompetitions) => friendsCompetitions[userId] || {}
);
export const selectCompetitionLoading = createSelector(
  selectCompetitionState,
  (state) => state.isLoading
);
export const selectCompetitionError = createSelector(
  selectCompetitionState,
  (state) => state.error
);
// Additional selectors for derived data
export const selectCompetitionsByType = (type: CompetitionType) => createSelector(
  selectCompetitions,
  selectCompetitionIds,
  (competitions, ids) => {
    return ids
      .map(id => competitions[id])
      .filter(comp => comp.type === type);
  }
);
export const selectCompetitionsByYear = createSelector(
  selectCompetitions,
  selectCompetitionIds,
  (competitions, ids) => {
    const result: Record<string, Competition[]> = {};
    
    ids.forEach(id => {
      const comp = competitions[id];
      const year = new Date(comp.date).getFullYear().toString();
      
      if (!result[year]) {
        result[year] = [];
      }
      
      result[year].push(comp);
    });
    
    return result;
  }
);
export const selectMedalCounts = createSelector(
  selectCompetitions,
  selectCompetitionIds,
  (competitions, ids) => {
    const counts = {
      gold: 0,
      silver: 0,
      bronze: 0,
      total: 0
    };
    
    ids.forEach(id => {
      const comp = competitions[id];
      if (comp.rank && comp.rank > 0) {
        // Count medals based on rank
        if (comp.rank === 1) counts.gold++;
        else if (comp.rank === 2) counts.silver++;
        else if (comp.rank === 3) counts.bronze++;
        
        // Increment total for any medal
        counts.total++;
      }
    });
    
    return counts;
  }
);
export const selectWinLossRecord = createSelector(
  selectCompetitions,
  selectCompetitionIds,
  (competitions, ids) => {
    let wins = 0;
    let losses = 0;
    
    ids.forEach(id => {
      const comp = competitions[id];
      // Safely add wins and losses with default 0 if undefined
      wins += comp.winsCount || 0;
      losses += comp.lossesCount || 0;
    });
    
    const total = wins + losses;
    const winPercentage = total > 0 ? (wins / total) * 100 : 0;
    
    return {
      wins,
      losses,
      total,
      winPercentage: Math.round(winPercentage * 10) / 10 // Round to 1 decimal place
    };
  }
);
export default competitionSlice.reducer;